/** Each state key's allowed values. The first value is the design, so the bare URL shows the design. */
export type StateOptions = { readonly [key: string]: readonly [string, ...Array<string>] };

/** One chosen value per state key. */
export type StateOf<O extends StateOptions> = { readonly [K in keyof O]: O[K][number] };

/**
 * The panel's states as query parameters. Only values that differ from the design go into the URL, so a link
 * shares exactly the combination on screen; parameters it does not own (`embed`, anything a page adds) are kept.
 */
export class PrototypeUrlState<O extends StateOptions> {
    private readonly options: O;

    constructor(options: O) {
        this.options = options;
    }

    public keys(): ReadonlyArray<keyof O & string> {
        return Object.keys(this.options);
    }

    public values(key: keyof O & string): readonly [string, ...Array<string>] {
        const values = this.options[key];
        if (values === undefined) {
            throw new Error(`Unknown prototype state "${key}"`);
        }

        return values;
    }

    public defaults(): StateOf<O> {
        return this.complete({});
    }

    /** The design's values with `partial` on top. */
    public complete(partial: Partial<StateOf<O>>): StateOf<O> {
        const state: Record<string, string> = {};
        for (const key of this.keys()) {
            state[key] = partial[key] ?? this.design(key);
        }

        return state as StateOf<O>;
    }

    public read(search: string): StateOf<O> {
        const params = new URLSearchParams(search);
        const state: Record<string, string> = {};
        for (const key of this.keys()) {
            const value = params.get(key);
            state[key] = value !== null && this.allows(key, value) ? value : this.design(key);
        }

        return state as StateOf<O>;
    }

    /** `search` with the state's parameters set; the result starts with `?`, or is empty. */
    public write(state: StateOf<O>, search: string): string {
        const params = new URLSearchParams(search);
        for (const key of this.keys()) {
            const value: string = state[key];
            if (value === this.design(key)) {
                params.delete(key);
            } else {
                params.set(key, value);
            }
        }

        const query = params.toString();
        return query ? `?${query}` : '';
    }

    /** The state with one key changed, or null when the key or the value is not one of the options. */
    public withValue(state: StateOf<O>, key: string, value: string): StateOf<O> | null {
        if (!this.isKey(key) || !this.allows(key, value)) {
            return null;
        }

        const changed: Record<string, string> = { [key]: value };
        return { ...state, ...changed };
    }

    public same(a: StateOf<O>, b: StateOf<O>): boolean {
        return this.keys().every(key => a[key] === b[key]);
    }

    public isKey(key: string): key is keyof O & string {
        return Object.prototype.hasOwnProperty.call(this.options, key);
    }

    public allows(key: keyof O & string, value: string): boolean {
        return this.values(key).indexOf(value) >= 0;
    }

    private design(key: keyof O & string): string {
        return this.values(key)[0];
    }
}
